"use client"

import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import type { Review } from "@/types/review"
import { ReviewCard } from "./review-card"
import { useEffect, useRef, useState } from "react"

interface ReviewCarouselProps {
  reviews: Review[]
  currentIndex: number
  itemsPerView: number
  onPrevSlide: () => void
  onNextSlide: () => void
  onSetIndex: (index: number) => void
  onCardClick: (review: Review) => void
  onMarkHelpful: (reviewId: string) => void
}

export function ReviewCarousel({
  reviews,
  currentIndex,
  itemsPerView,
  onPrevSlide,
  onNextSlide,
  onSetIndex,
  onCardClick,
  onMarkHelpful,
}: ReviewCarouselProps) {
  const maxIndex = Math.max(0, reviews.length - itemsPerView)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Handle scroll events to update current index
  useEffect(() => {
    if (!isMobile || !containerRef.current) return

    const container = containerRef.current
    let scrollTimeout: NodeJS.Timeout

    const handleScroll = () => {
      if (isScrolling) return

      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const cardWidth = container.offsetWidth
        const scrollPosition = container.scrollLeft
        const newIndex = Math.round(scrollPosition / cardWidth)

        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reviews.length) {
          onSetIndex(newIndex)
        }
      }, 150)
    }

    container.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      container.removeEventListener("scroll", handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [currentIndex, isMobile, isScrolling, onSetIndex, reviews.length])

  // Update scroll position when currentIndex changes programmatically
  useEffect(() => {
    if (isMobile && containerRef.current) {
      setIsScrolling(true)
      const cardWidth = containerRef.current.offsetWidth
      containerRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: "smooth",
      })

      // Reset scrolling flag after animation
      setTimeout(() => {
        setIsScrolling(false)
      }, 500)
    }
  }, [currentIndex, isMobile])

  if (isMobile) {
    return (
      <>
        <div className="relative">
          <div
            ref={containerRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 md:px-4"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {reviews.map((review) => (
              <div key={review.id} className="flex-shrink-0 snap-center w-full" style={{ minWidth: "100%" }}>
                <ReviewCard review={review} onCardClick={onCardClick} onMarkHelpful={onMarkHelpful} />
              </div>
            ))}
          </div>
        </div>

        {reviews.length > 1 && (
          <div className="flex justify-center mt-6">
            <div className="flex gap-2">
              {Array.from({ length: reviews.length }, (_, i) => (
                <button
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? "bg-blue-600" : "bg-gray-400"}`}
                  onClick={() => onSetIndex(i)}
                />
              ))}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div className={`relative ${reviews.length > itemsPerView ? "px-12" : ""}`}>
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
              width: `${(reviews.length / itemsPerView) * 100}%`,
              gap: "1.5rem",
            }}
          >
            {reviews.map((review) => (
              <div
                key={review.id}
                className="flex-shrink-0"
                style={{
                  width: `calc(${100 / reviews.length}% - ${(1.5 * (itemsPerView - 1)) / itemsPerView}rem)`,
                }}
              >
                <ReviewCard review={review} onCardClick={onCardClick} onMarkHelpful={onMarkHelpful} />
              </div>
            ))}
          </div>
        </div>

        {reviews.length > itemsPerView && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white shadow-md border-gray-400 z-10"
              onClick={onPrevSlide}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white shadow-md border-gray-400 z-10"
              onClick={onNextSlide}
              disabled={currentIndex === maxIndex}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      {maxIndex > 0 && (
        <div className="flex justify-center mt-8">
          <div className="flex gap-2">
            {Array.from({ length: maxIndex + 1 }, (_, i) => (
              <button
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? "bg-blue-600" : "bg-gray-400"}`}
                onClick={() => onSetIndex(i)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}
